import { useEffect } from 'react';
import { useCampaignUiStore } from '@discovr/features';

/**
 * Publishes this roster tab's live pageSize/totalPages into the shared
 * campaign UI store. The admin-header page-kind share button has no table
 * state of its own — it reads these two fields back out (via useCampaignShare's
 * ShortShareInput.pageSize/totalPages) to fold them into the mint payload when
 * the visitor picks "Anyone with the link".
 *
 * Clears the store fields back to null on unmount so switching away from a
 * roster tab (e.g. to Details) doesn't leave a stale pageSize/totalPages
 * behind for a surface that has no pagination at all.
 *
 * `enabled` (default `true`) makes this hook a complete no-op — it never
 * touches the store at all, in either direction — when `false`. Callers in
 * public read-only mode (e.g. CreatorsTab.tsx) must pass `enabled: false`:
 * the "admin-header page-kind share button" this feeds doesn't exist in the
 * public shell, and the values it WOULD publish there are the disabled
 * roster query's own (permanently wrong: pageCount stuck at 1) numbers, not
 * the real grant snapshot — writing them into the shared store is worse than
 * writing nothing.
 */
export function usePublishSharePageInfo(
  pageSize: number,
  totalPages: number,
  enabled = true,
): void {
  const setSharePageInfo = useCampaignUiStore((s) => s.setSharePageInfo);

  useEffect(() => {
    if (!enabled) return;
    setSharePageInfo(pageSize, totalPages);
    return () => setSharePageInfo(null, null);
  }, [pageSize, totalPages, enabled, setSharePageInfo]);
}
